import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Link,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  Instagram,
  LinkedIn,
  LocationOn,
  Phone,
  Email,
  WhatsApp,
  QrCode2,
  Restaurant,
  RestaurantMenu,
} from "@mui/icons-material";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: "Home", path: "/" },
    { label: "Restaurants", path:"/restaurants"},
    { label: "About Us", path: "/about" },
    // { label: "How It Works", path: "/how-it-works" },
    { label: "Features", path: "/features" },
    { label: "Pricing", path: "/pricing" },
    { label: "Contact", path: "/contact" },
  ];

  const restaurantLinks = [
    { label: "Register Restaurant", path: "/register" },
    { label: "Restaurant Dashboard", path: "/dashboard" },
    { label: "Menu Management", path: "/menu" },
    { label: "Order Management", path: "/orders" },
    { label: "QR Code Generator", path: "/qr-generator" },
    { label: "Staff Management", path: "/staff" },
  ];

  const supportLinks = [
    { label: "Help Center", path: "/help" },
    { label: "FAQ", path: "/faq" },
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms of Service", path: "/terms-of-service" },
  ];

  const socialIcons = [
    { icon: <Instagram />, url: "https://www.instagram.com/restaurantwala.in?utm_source=qr&xtok=MjB1bjAyYXRoNXpu", color: "#E4405F" },
    { icon: <WhatsApp />, url: "https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services.", color: "#25D366" },
    
    // { icon: <Facebook />, url: "https://facebook.com", color: "#1877F2" },
    // { icon: <LinkedIn />, url: "https://linkedin.com", color: "#0A66C2" },
    // { icon: <YouTube />, url: "https://youtube.com", color: "#FF0000" },
  ];

  const paymentMethods = [
    "💳 Credit Card",
    "📱 UPI",
    "📲 PhonePe",
    "🏦 Net Banking",
    "💰 Cash",
    "📱 Google Pay",
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#171616",
        color: "#fff",
        pt: { xs: 6, sm: 8, md: 10 },
        pb: { xs: 3, md: 4 },
        mt: "auto",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Top Border */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, #FF6B00, #E85A00, #49d75b)",
          backgroundSize: "200% 100%",
          animation: "gradientMove 3s ease-in-out infinite",
        }}
      />

      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(73,215,91,0.03) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(73,215,91,0.03) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xl">
        {/* Main Footer Grid */}
        <Grid
          container
          // spacing={{ xs: 4, sm: 5, md: 6 }}
          spacing={{ xs: 3, md: 4 }}
          sx={{ mb: { xs: 3, md: 4 } }}
        >
          {/* Brand Section */}
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <RestaurantMenu
                sx={{
                  color: "#FF6B00",
                  fontSize: { xs: 32, md: 40 },
                  mr: 1.5,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: "#fff",
                  fontSize: { xs: "1.3rem", md: "1.8rem" },
                }}
              >
                Restaurant
                <Box component="span" sx={{ color: "#E85A00" }}>
                  Wala
                </Box>
              </Typography>
            </Box>

            <Typography
              sx={{
                color: "#999",
                fontSize: { xs: "13px", md: "14px" },
                lineHeight: 1.8,
                maxWidth: "320px",
                mb: 3,
              }}
            >
              Complete QR-based ordering and restaurant management SaaS
              platform. Empowering restaurants to digitize their operations and
              grow their business.
            </Typography>

            {/* Social Icons */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {socialIcons.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.url}
                  target="_blank"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "#999",
                    width: { xs: 36, md: 40 },
                    height: { xs: 36, md: 40 },
                    "&:hover": {
                      bgcolor: `${social.color}25`,
                      color: social.color,
                      transform: "translateY(-3px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", md: "1.1rem" },
                mb: { xs: 2, md: 3 },
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 20,
                  bgcolor: "#E85A00",
                  borderRadius: "2px",
                }}
              />
              Quick Links
            </Typography>

            <Stack spacing={{ xs: 1.5, md: 2 }}>
              {quickLinks?.map((item) => (
                <Link
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: "#999",
                    textDecoration: "none",
                    fontSize: { xs: "13px", md: "14px" },
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      color: "#E85A00",
                      transform: "translateX(5px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      bgcolor: "#E85A00",
                      opacity: 0,
                      transition: "all 0.3s ease",
                      ".MuiLink-root:hover &": { opacity: 1 },
                    }}
                  />
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Support Links */}
          <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", md: "1.1rem" },
                mb: { xs: 2, md: 3 },
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 20,
                  bgcolor: "#E85A00",
                  borderRadius: "2px",
                }}
              />
              Support
            </Typography>

            <Stack spacing={{ xs: 1.5, md: 2 }}>
              {supportLinks.map((item) => (
                <Link
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: "#999",
                    textDecoration: "none",
                    fontSize: { xs: "13px", md: "14px" },
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      color: "#E85A00",
                      transform: "translateX(5px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      bgcolor: "#E85A00",
                      opacity: 0,
                      transition: "all 0.3s ease",
                      ".MuiLink-root:hover &": { opacity: 1 },
                    }}
                  />
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact & Newsletter */}
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", md: "1.1rem" },
                mb: { xs: 2, md: 3 },
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 20,
                  bgcolor: "#E85A00",
                  borderRadius: "2px",
                }}
              />
              Get In Touch
            </Typography>

            <Stack spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 3, md: 4 } }}>
              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <LocationOn
                  sx={{ color: "#E85A00", fontSize: { xs: 20, md: 22 } }}
                />
                <Typography
                  sx={{ color: "#999", fontSize: { xs: "13px", md: "14px" } }}
                >
                  New Delhi, India
                </Typography>
              </Box> */}

              {/* Head Office */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <LocationOn
                  sx={{
                    color: "#E85A00",
                    fontSize: { xs: 20, md: 22 },
                    mt: 0.3,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    color: "#999",
                    fontSize: { xs: "13px", md: "14px" },
                    lineHeight: 1.6,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      color: "#fff",
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    Head Office
                  </Box>
                  Shiv Manglam 6, AB 23, Kalwar Rd,
                  <br />
                  Govindpura, Jhotwara, Hathod,
                  <br />
                  Jaipur, Rajasthan - 302013
                </Typography>
              </Box>

              {/* Branch Office */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <LocationOn
                  sx={{
                    color: "#E85A00",
                    fontSize: { xs: 20, md: 22 },
                    mt: 0.3,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    color: "#999",
                    fontSize: { xs: "13px", md: "14px" },
                    lineHeight: 1.6,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      color: "#fff",
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    Branch Office
                  </Box>
                  Tharhounda Showroom, Gawdi Mod,
                  <br />
                  Neem Ka Thana, Rajasthan - 332713
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Phone
                  sx={{ color: "#E85A00", fontSize: { xs: 20, md: 22 } }}
                />
                <Typography
                  sx={{ color: "#999", fontSize: { xs: "13px", md: "14px" } }}
                >
                  +91 6378788756
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Email
                  sx={{ color: "#E85A00", fontSize: { xs: 20, md: 22 } }}
                />
                <Typography
                  sx={{ color: "#999", fontSize: { xs: "13px", md: "14px" } }}
                >
                  restaurantwala24@gmail.com
                </Typography>
              </Box>

              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <WhatsApp
                  sx={{ color: "#E85A00", fontSize: { xs: 20, md: 22 } }}
                />
                <Typography
                  sx={{ color: "#999", fontSize: { xs: "13px", md: "14px" } }}
                >
                  +91 6378788756
                </Typography>
              </Box> */}
            </Stack>
          </Grid>
        </Grid>
        {/* Divider */}
        <Divider
          sx={{
            my: { xs: 3, md: 4 },
            borderColor: "rgba(255,255,255,0.05)",
          }}
        />

        {/* Bottom Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography
              sx={{
                color: "#666",
                fontSize: { xs: "12px", md: "13px" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              © {currentYear} RestaurantWala. All Rights Reserved.
              {/* <Box
                component="span"
                sx={{
                  display: { xs: "block", sm: "inline" },
                  color: "#E85A00",
                  fontWeight: 600,
                }}
              >
                {" "}
                Made with ❤️ in India
              </Box> */}
            </Typography>
          </Grid>

          {/* <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, md: 2 },
                justifyContent: { xs: "center", sm: "flex-end" },
                flexWrap: "wrap",
              }}
            >
              <Link
                component={RouterLink}
                to="/privacy-policy"
                sx={{
                  color: "#666",
                  textDecoration: "none",
                  fontSize: { xs: "11px", md: "12px" },
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#E85A00",
                  },
                }}
              >
                Privacy
              </Link>
              <Link
                component={RouterLink}
                to="/terms-of-service"
                sx={{
                  color: "#666",
                  textDecoration: "none",
                  fontSize: { xs: "11px", md: "12px" },
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#E85A00",
                  },
                }}
              >
                Terms
              </Link>
            </Box>
          </Grid> */}
        </Grid>
      </Container>
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </Box>
  );
};

export default Footer;
